import * as React from 'react';
import { withRouter } from '../common/with-router';
import { useNavigate } from 'react-router-dom';
import TaskService from '../services/task.service';
import { Box, Button, Dialog, Grid, Paper, Checkbox, Snackbar, SnackbarContent, IconButton, Tooltip, Typography, Popover } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Title from './title.component';
import { DataGrid } from '@mui/x-data-grid';
import { convertToShortDate } from '../common/actions';
import { convertDateToSlash, convertDateSlashToHyphen } from '../common/validateEvents';
import TaskDialog from './taskDialog.component';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LaunchIcon from '@mui/icons-material/Launch';

function Calendar (props) {
    const navigate = useNavigate();
    const [tasks, setTasks] = React.useState([]);
    const [task, setTask] = React.useState({});
    const [taskOpen, setTaskOpen] = React.useState(false);
    const [reload, setReload] = React.useState(false);
    const [showingComplete, setShowComplete] = React.useState(false);
    const [snackOpen, setSnackOpen] = React.useState(false);
    const [snackMessage, setSnackMessage] = React.useState('');
    const [currentDate, setCurrentDate] = React.useState(new Date());
    const [taskCounts, setTaskCounts] = React.useState({});
    const [view, setView] = React.useState('calendar'); // 'calendar' or 'list'
    const [filterModel, setFilterModel] = React.useState({
      items: [{ field: 'completed', operator: 'equals', value: 'Incomplete' }],
    });
    const [sortModel, setSortModel] = React.useState([
      {
        field: 'date',
        sort: 'asc',
      },
    ]);
    const [selectedDate, setSelectedDate] = React.useState(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [dateTasks, setDateTasks] = React.useState([]);

    const handleSnackClose = () => {
        setSnackOpen(false);
    };

    const canJumpFromSnackbar = (snackMessage) => {
        let searchString = 'to cage #';
        if(snackMessage.includes(searchString)){
            let cageNum = snackMessage.split(searchString)[1].replace(/^\D+/g, '');
            return cageNum;
        }
    };

    const daysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const firstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    };

    const getMonthName = (date) => {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const getTaskCounts = (tasks) => {
        const counts = {};
        tasks.forEach(task => {
            const date = new Date(task.date);
            const dateStr = date.toISOString().split('T')[0];
            if (!counts[dateStr]) {
                counts[dateStr] = { completed: 0, incomplete: 0 };
            }
            if (task.completed === 'Completed') {
                counts[dateStr].completed++;
            } else {
                counts[dateStr].incomplete++;
            }
        });
        return counts;
    };

    React.useEffect(() => {
        TaskService.getAllTasks()
        .then((response) => {
            let _tasks = response.data;
            _tasks.forEach((task) => {
                task.date = convertToShortDate(convertDateToSlash(task.date));
                task.completed = (task.completed ? 'Completed' : 'Incomplete')
            });
            // Filter out tasks with type 'pup snax' and 'breeder snax'
            _tasks = _tasks.filter(task => 
                task.type.toLowerCase() !== 'pup snax' && 
                task.type.toLowerCase() !== 'breeder snax'
            );
            setTasks(_tasks);
            setTaskCounts(getTaskCounts(_tasks));
        })
    },[props.reload, reload]);

    const handleRowClick = (row) => {
        setTask(row);
        setTaskOpen(true);
    };

    const handleClose = () => {
        setTaskOpen(false);
    };

    const handleTaskUpdate = React.useCallback((newTask) => {
      TaskService.updateTask(newTask);
      props.setReload(!props.reload);
      setReload(!reload)
    });

    const handleComplete = React.useCallback((currentTask) => {
        // Toggle the completed status
        currentTask.completed = currentTask.completed === 'Completed' ? 'Incomplete' : 'Completed';
        
        // Convert date format for backend
        currentTask.date = convertDateSlashToHyphen(currentTask.date) + 'T04:00:00';
        
        // Convert completed status to boolean for backend
        const taskForBackend = {
            ...currentTask,
            completed: currentTask.completed === 'Completed'
        };
        
        // Update task in backend
        TaskService.updateTask(taskForBackend)
            .then(() => {
                const message = `Task ${currentTask.type} for Cage #${currentTask.cageId} marked as ${currentTask.completed}`;
                setSnackMessage(message);
                setSnackOpen(true);
                props.setReload(!props.reload);
                setReload(!reload);
            })
            .catch(error => {
                console.error("Error updating task:", error);
            });
    });

    const handleDateClick = (event, date) => {
        const tasksForDate = tasks.filter(task => {
            const taskDate = new Date(task.date);
            return taskDate.toDateString() === date.toDateString();
        });
        setDateTasks(tasksForDate);
        setSelectedDate(date);
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setSelectedDate(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'date-popover' : undefined;

    const columns = [
        {field: 'id', headerName: 'taskId', width: 125, hide: true},
        {
            field: 'completed',
            headerName: '',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Checkbox
                        style={{
                            transform: "scale(.9)",
                        }}
                        sx={{p:0, m:0}}
                        icon={<CheckBoxOutlineBlankIcon/>}
                        checkedIcon={<CheckBoxIcon/>}
                        checked={params.value === 'Completed'}
                        onClick={(event) => {
                            event.stopPropagation(); // Prevent row click event
                            handleComplete(params.row);
                        }}
                    />
                </Box>
            )
        },
        {field: 'date', headerName: 'Date', width: 125, align: 'center', headerAlign: 'center'},
        {field: 'roomId', headerName: 'Room #', width: 125, align: 'center', headerAlign: 'center'},
        {field: 'cageId', headerName: 'Cage #', width: 125, align: 'center', headerAlign: 'center'},
        {field: 'type', headerName: 'Type', width: 175, align: 'center', headerAlign: 'center'}
    ];
    
    const toggleFilterSortModel = () => {
      setShowComplete(!showingComplete);
      if(!showingComplete){
        setSortModel([
          {
            field: 'date',
            sort: 'desc'
          }
        ]);
        setFilterModel({
          items: [{ field: 'completed', operator: 'equals', value: 'Completed' }],
        })
      }
      else if(showingComplete){
        setSortModel([
          {
            field: 'date',
            sort: 'asc'
          }
        ]);
        setFilterModel({
          items: [{ field: 'completed', operator: 'equals', value: 'Incomplete' }],
        })
      }
    }

    const prevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    };

    const renderCalendar = () => {
        const days = [];
        const totalDays = daysInMonth(currentDate);
        const firstDay = firstDayOfMonth(currentDate);

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < firstDay; i++) {
            days.push(<Box key={`empty-${i}`} sx={{ p: 2, border: '1px solid #e0e0e0' }} />);
        }

        // Add cells for each day of the month
        for (let i = 1; i <= totalDays; i++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
            const dateStr = date.toISOString().split('T')[0];
            const counts = taskCounts[dateStr] || { completed: 0, incomplete: 0 };
            const totalTasks = counts.completed + counts.incomplete;

            days.push(
                <Box
                    key={i}
                    onClick={(event) => handleDateClick(event, date)}
                    sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        minHeight: '100px',
                        position: 'relative',
                        cursor: totalTasks > 0 ? 'pointer' : 'default',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <Typography variant="body2">{i}</Typography>
                    {totalTasks > 0 && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            {counts.incomplete > 0 && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        backgroundColor: '#1976d2',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {counts.incomplete}
                                </Typography>
                            )}
                            {counts.completed > 0 && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        backgroundColor: '#2e7d32',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: '24px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {counts.completed}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            );
        }

        return days;
    };

    return(
        <>
          <Grid item xs={12} md={12} lg={12}>
            <Paper
            sx={{
                p:2,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '80vh'
            }}>
              <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1
              }}>
                <Title>Tasks</Title>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button size='small' sx={{borderRadius: 4}} onClick={toggleFilterSortModel} variant={showingComplete ? 'contained': 'outlined'}>
                        Showing Completed Tasks
                    </Button>
                    <Button size='small' sx={{borderRadius: 4}} onClick={() => setView(view === 'calendar' ? 'list' : 'calendar')} variant={view === 'calendar' ? 'contained': 'outlined'}>
                        {view === 'calendar' ? 'Show List' : 'Show Calendar'}
                    </Button>
                </Box>
              </Box>
              {view === 'calendar' ? (
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={prevMonth}>
                            <ChevronLeft />
                        </IconButton>
                        <Typography variant="h6">{getMonthName(currentDate)}</Typography>
                        <IconButton onClick={nextMonth}>
                            <ChevronRight />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gap: 1,
                        }}
                    >
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                            <Box
                                key={day}
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    borderBottom: '2px solid #e0e0e0',
                                }}
                            >
                                {day}
                            </Box>
                        ))}
                        {renderCalendar()}
                    </Box>
                </Box>
              ) : (
                <DataGrid
                rows={tasks}
                columns={columns}
                columnVisibilityModel={{
                    id: false
                }}
                checkboxSelection={false}
                disableRowSelectionOnClick={true}
                onRowDoubleClick={(params) => {
                    handleRowClick(params.row)
                }}
                filterModel={filterModel}
                onFilterModelChange={(newFilterModel) => {setFilterModel(newFilterModel)}}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                sx={{
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
                        outline: "none"
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell": {
                        whiteSpace: "normal !important",
                        wordWrap: "break-word !important"
                    },
                    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                        height: '0.5em'
                      },
                      '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                        boxShadow: 'inset 0 0 6px rgba(0,0,0,0.05)',
                        webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.05)'
                      },
                      '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.2)',
                      },
                    maxHeight: '67vh'
                    }}>
                </DataGrid>
              )}
            </Paper>
          </Grid>
          <Dialog open={taskOpen} onClose={handleClose}>
            <TaskDialog task={task} onSubmit={handleTaskUpdate} onClose={handleClose}/>
          </Dialog>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
          >
            <Box sx={{ p: 2, minWidth: 300 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Tasks for {selectedDate?.toLocaleDateString()}
                </Typography>
                {dateTasks.length === 0 ? (
                    <Typography>No tasks for this day</Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {dateTasks.map((task) => (
                            <Box
                                key={task.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                }}
                            >
                                <Box>
                                    <Typography variant="body2">
                                        {task.type} - Cage #{task.cageId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Room #{task.roomId}
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={task.completed === 'Completed'}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleComplete(task);
                                    }}
                                    icon={<CheckBoxOutlineBlankIcon />}
                                    checkedIcon={<CheckBoxIcon />}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
          </Popover>
          <Snackbar
            open={snackOpen}
            autoHideDuration={6000}
            onClose={handleSnackClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <SnackbarContent
              message={snackMessage}
              action={
                <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackClose}>
                  <LaunchIcon fontSize="small" />
                </IconButton>
              }
            />
          </Snackbar>
        </>
    )
}

export default withRouter(Calendar)